import { setPersonalInfo, setPhoneNumber as setStorePhoneNumber } from '../../../../store/signUp/slice';
import { City, Country, CountryState } from '../../../../store/signUp/types';
import { DayValue } from '@amir04lm26/react-modern-calendar-date-picker';
import PhoneNumberInput from '../../../../components/PhoneNumberInput';
import { useDispatch, useSelector } from '../../../../store/store';
import { errorMessages } from '../../../../config/notification';
import MotionWrapper from '../../../../containers/MotionWrapper';
import DatePicker from '../../../../components/DatePicker';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import { useEffect, useState } from 'react';
import styles from './style.module.scss';
import {
    toDatePickerFormat,
    birthDateValidation,
    fromDatePickerFormat,
} from '../../../../utils/dateHelpers';
import {
    getStatesThunk,
    getCountriesThunk,
    updatePersonalInfoThunk,
} from '../../../../store/signUp/thunks';
import {
    selectStates,
    selectCities,
    selectEditMode,
    selectUserEmail,
    selectCountries,
    selectFetchStatus,
    selectPersonalInfo,
    selectDisablePhoneNumber,
} from '../../../../store/signUp/selectors';

function PersonalInfo() {
    const dispatch = useDispatch();
    const cities = useSelector(selectCities);
    const states = useSelector(selectStates);
    const email = useSelector(selectUserEmail);
    const editMode = useSelector(selectEditMode);
    const countries = useSelector(selectCountries);
    const fetchStatus = useSelector(selectFetchStatus);
    const personalInfo = useSelector(selectPersonalInfo);
    const [city, setCity] = useState(personalInfo?.City || '');
    const [state, setState] = useState(personalInfo?.State || '');
    const disablePhoneNumber = useSelector(selectDisablePhoneNumber);
    const [address, setAddress] = useState(personalInfo?.Address || '');
    const [country, setCountry] = useState(personalInfo?.Country || '');
    const [zipCode, setZipCode] = useState(personalInfo?.Zip_Code || '');
    const [lastName, setLastName] = useState(personalInfo?.LastName || '');
    const [firstName, setFirstName] = useState(personalInfo?.FirstName || '');
    const [linkedinLink, setLinkedinLink] = useState(personalInfo?.LinkdIn_Link || '');
    const [preferredName, setPreferredName] = useState(personalInfo?.Preferred_Name || '');
    const [phoneNumber, setPhoneNumber] = useState<string | undefined>(personalInfo?.Phone_Number);
    const [DOB, setDOB] = useState<DayValue>(personalInfo?.DOB ? toDatePickerFormat(personalInfo?.DOB) : null);
    const [isValidURL, setIsValidURL] = useState(true);
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false);

    useEffect(() => {
        dispatch(getCountriesThunk());
        dispatch(getStatesThunk());
    }, []);

    // useEffect(() => {
    //     city.length > 1 && dispatch(getCitiesThunk(city));
    // }, [city]);

    const next = () => {
        if (!phoneNumber) return;
        const info = {
            City: city,
            Email: email,
            State: state,
            Country: country,
            Address: address,
            Phone_Number: phoneNumber,
            Zip_Code: zipCode,
            LinkdIn_Link: linkedinLink,
            Preferred_Name: preferredName,
            FirstName: firstName,
            LastName: lastName,
            DOB: fromDatePickerFormat(DOB),
        };

        dispatch(setPersonalInfo(info));
        dispatch(setStorePhoneNumber(phoneNumber));
        dispatch(updatePersonalInfoThunk({
            city,
            email,
            state,
            zipCode,
            address,
            country,
            lastName,
            firstName,
            phoneNumber,
            linkedinLink,
            preferredName,
            birthDate: fromDatePickerFormat(DOB),
        }));
    };

    const setCityAndState = (city: City) => {
        setCity(city.city);
        const state = states.find(state => `${state.id}` === city.state_id);
        state && setState(state.state);
        setCountry('United States');
    };

    return (
        <MotionWrapper className={styles.wrapper}>
            <div className={styles.row}>
                <Input
                    isRequired
                    label='Legal first name'
                    type='text'
                    placeholder=''
                    value={firstName}
                    onChange={setFirstName}
                />
                <Input
                    isRequired
                    label='Legal last name'
                    type='text'
                    placeholder=''
                    value={lastName}
                    onChange={setLastName}
                />
            </div>

            <div className={styles.row}>
                <Input
                    label='Preferred name (Optional)'
                    type='text'
                    placeholder=''
                    value={preferredName}
                    onChange={setPreferredName}
                />
                <PhoneNumberInput
                    isRequired
                    setIsValid={setIsValidPhoneNumber}
                    label='Phone number'
                    showErrorMessage
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    disabled={editMode || disablePhoneNumber}
                />
            </div>

            <div className={styles.row}>
                <Input
                    isRequired
                    disabled
                    label='Email'
                    type='email'
                    value={email}
                    validation='Email'
                    placeholder=''
                />
                <DatePicker
                    isRequired
                    showErrorMessage
                    selectedDate={DOB}
                    label='Date of birth'
                    placeholder='MM-DD-YYYY'
                    setSelectedDate={setDOB}
                    errorMessage={(DOB && !birthDateValidation(DOB)) ? errorMessages.birthDate : ''}
                />
            </div>

            <div className={styles.row}>
                <Input
                    isRequired
                    type='text'
                    label='Address'
                    placeholder=''
                    value={address}
                    onChange={setAddress}
                />
                <Input
                    isRequired
                    listShow
                    label='City'
                    type='text'
                    value={city}
                    list={cities}
                    placeholder=''
                    listIdKey='id'
                    onChange={setCity}
                    listTitleKey='city'
                    listTitleSuffixKey='state_code'
                    onItemClick={(item: City) => setCityAndState(item)}
                />
            </div>

            <div className={styles.row}>
                <Input
                    isRequired
                    listShow
                    type='text'
                    placeholder=''
                    listIdKey='id'
                    label='Country'
                    value={country}
                    listTitleKey='title'
                    onChange={setCountry}
                    onItemClick={(item: Country) => setCountry(item.title)}
                    list={countries.filter(i => i.title.toLowerCase().startsWith(country.toLowerCase()))}
                />
                <Input
                    isRequired
                    listShow
                    type='text'
                    label='State'
                    value={state}
                    placeholder=''
                    listIdKey='id'
                    listTitleKey='state'
                    onChange={setState}
                    onItemClick={(item: CountryState) => setState(item.state)}
                    list={states.filter(i => i.state.toLowerCase().startsWith(state.toLowerCase()))}
                />
            </div>

            <div className={styles.row}>
                <Input
                    isRequired
                    type='text'
                    label='Zip code'
                    placeholder=''
                    value={zipCode}
                    onChange={setZipCode}
                />
                <Input
                    label='Linkedin link (Optional)'
                    type='text'
                    placeholder='Enter linkedin link'
                    value={linkedinLink}
                    onChange={setLinkedinLink}
                    validation='URL'
                    setIsValid={setIsValidURL}
                />
            </div>

            <Button
                onClick={next}
                className={styles.CTA}
                text={editMode ? 'Save' : 'Continue'}
                loading={fetchStatus === 'Pending'}
                disabled={
                    !city ||
                    !state ||
                    !address ||
                    !zipCode ||
                    !lastName ||
                    !firstName ||
                    !isValidURL ||
                    !isValidPhoneNumber ||
                    !birthDateValidation(DOB)
                }
            />
        </MotionWrapper>
    );
};

export default PersonalInfo;
